import React, { useRef, useState, useEffect } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select, { MultiValue } from 'react-select';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import { FundRequestStatus } from '~/enums/fundRequest';
import {
    fetchFundRequestCategories,
    fetchVendors,
} from '~/services/fund-requests';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, isDefaultSelection, parseVisibleColumns } from './fundRequestColumns';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface SelectOption {
    value: string;
    label: string;
}

interface VendorOption {
    value: number;
    label: string;
}

// Date range preset options
interface DatePreset {
    value: string;
    label: string;
    getRange: () => { start: string; end: string };
}

const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

// Format a number as currency display (e.g., "$1,234.56")
const formatCurrency = (value: string): string => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    if (!numericValue) return '';

    // Parse as float and format
    const num = parseFloat(numericValue);
    if (isNaN(num)) return '';

    return (
        '$' +
        num.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })
    );
};

// Parse currency string back to raw number string for API
const parseCurrencyToNumber = (value: string): string => {
    // Remove $ and commas, keep only numbers and decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    return numericValue;
};

const getDatePresets = (): DatePreset[] => {
    const today = new Date();

    return [
        {
            value: '',
            label: 'All time',
            getRange: () => ({ start: '', end: '' }),
        },
        {
            value: 'last_1_day',
            label: '1 Day',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 1);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_7_days',
            label: '7 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 7);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_30_days',
            label: '1 Month',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_6_months',
            label: '6 Months',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setMonth(start.getMonth() - 6);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'ytd',
            label: 'YTD',
            getRange: () => {
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_1_year',
            label: '1 Year',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setFullYear(start.getFullYear() - 1);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_5_years',
            label: '5 Years',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setFullYear(start.getFullYear() - 5);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'current_fiscal_year',
            label: 'Current Fiscal Year',
            getRange: () => {
                const fyStartYear =
                    today.getMonth() >= 9
                        ? today.getFullYear()
                        : today.getFullYear() - 1;
                const start = new Date(fyStartYear, 9, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'custom',
            label: 'Custom',
            getRange: () => ({ start: '', end: '' }),
        },
    ];
};

// Status options based on FundRequestStatus enum
const statusOptions: SelectOption[] = [
    { value: '', label: 'Any' },
    { value: FundRequestStatus.OPEN, label: 'Open' },
    { value: FundRequestStatus.IN_REVIEW, label: 'In Review' },
    { value: FundRequestStatus.NEEDS_INFO, label: 'Needs Info' },
    { value: FundRequestStatus.AP_REVIEW, label: 'AP Review' },
    { value: FundRequestStatus.FOR_DOF_APPROVAL, label: 'For DOF Approval' },
    { value: FundRequestStatus.APPEALED, label: 'Appealed' },
    { value: FundRequestStatus.REJECTED, label: 'Rejected' },
    { value: FundRequestStatus.PAID, label: 'Paid' },
    { value: FundRequestStatus.PENDING, label: 'Pending' },
];

// Source options (staff vs web/client)
const sourceOptions: SelectOption[] = [
    { value: '', label: 'All' },
    { value: 'web', label: 'Web' },
    { value: 'staff', label: 'Manually Entered' },
];

// Time scale options for react-select
const timeScaleOptions: SelectOption[] = [
    { value: '', label: 'All time' },
    { value: 'last_1_day', label: '1 Day' },
    { value: 'last_7_days', label: '7 Days' },
    { value: 'last_30_days', label: '1 Month' },
    { value: 'last_6_months', label: '6 Months' },
    { value: 'ytd', label: 'YTD' },
    { value: 'last_1_year', label: '1 Year' },
    { value: 'last_5_years', label: '5 Years' },
    { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
    { value: 'custom', label: 'Custom' },
];

const EMPTY_FORM_VALUES = {
    client_name: '',
    ntaf_id: '',
    ap_invoice_number: '',
    payee: '',
    vendor_id: '',
    status: '',
    source: '',
    amount_min: '',
    amount_max: '',
    created_start_date: '',
    created_end_date: '',
    updated_start_date: '',
    updated_end_date: '',
};

const FundRequestAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();

    // Form state
    const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

    // Multi-select states
    const [selectedExpenseTypes, setSelectedExpenseTypes] = useState<
        SelectOption[]
    >([]);
    const [expenseTypeOptions, setExpenseTypeOptions] = useState<
        SelectOption[]
    >([]);
    const [isLoadingExpenseTypes, setIsLoadingExpenseTypes] = useState(false);

    // Vendor search
    const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(
        null,
    );
    const [vendorSearch, setVendorSearch] = useState('');
    const [isLoadingVendors, setIsLoadingVendors] = useState(false);

    // Date presets
    const [selectedTimeScale, setSelectedTimeScale] = useState('');
    const [selectedCreatedPreset, setSelectedCreatedPreset] = useState('');
    const [selectedUpdatedPreset, setSelectedUpdatedPreset] = useState('');

    // Column picker: seed from the URL (Back-to-Search round-trip), else defaults.
    const [showColumns, setShowColumns] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
        );
    };

    const datePresets = getDatePresets();

    // Load expense type options
    useEffect(() => {
        const loadExpenseTypes = async () => {
            setIsLoadingExpenseTypes(true);
            try {
                const categories = await fetchFundRequestCategories();
                const options = categories.map(
                    (cat: { value: string; label: string }) => ({
                        value: cat.value,
                        label: cat.label,
                    }),
                );
                setExpenseTypeOptions(options);
            } catch (error) {
                console.error('Error fetching expense types:', error);
            } finally {
                setIsLoadingExpenseTypes(false);
            }
        };
        loadExpenseTypes();
    }, []);

    // Search vendors with debounce
    useEffect(() => {
        if (vendorSearch.length < 2) {
            setVendorOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setIsLoadingVendors(true);
            try {
                const vendors = await fetchVendors(vendorSearch);
                const options = vendors.map(
                    (v: { id: number; name: string }) => ({
                        value: v.id,
                        label: v.name,
                    }),
                );
                setVendorOptions(options);
            } catch (error) {
                console.error('Error fetching vendors:', error);
            } finally {
                setIsLoadingVendors(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [vendorSearch]);

    // Populate form from URL params
    useEffect(() => {
        if (searchParams.size > 0) {
            setFormValues({
                client_name: searchParams.get('client_name') || '',
                ntaf_id: searchParams.get('ntaf_id') || '',
                ap_invoice_number: searchParams.get('ap_invoice_number') || '',
                payee: searchParams.get('payee') || '',
                vendor_id: searchParams.get('vendor_id') || '',
                status: searchParams.get('status') || '',
                source: searchParams.get('source') || '',
                amount_min: searchParams.get('amount_min') || '',
                amount_max: searchParams.get('amount_max') || '',
                created_start_date:
                    searchParams.get('created_date_start') || '',
                created_end_date: searchParams.get('created_date_end') || '',
                updated_start_date:
                    searchParams.get('updated_date_start') || '',
                updated_end_date: searchParams.get('updated_date_end') || '',
            });

            // Populate expense types
            const expenseTypeParams = searchParams.getAll('expense_type');
            if (expenseTypeParams.length > 0 && expenseTypeOptions.length > 0) {
                const selected = expenseTypeOptions.filter((opt) =>
                    expenseTypeParams.includes(opt.value),
                );
                setSelectedExpenseTypes(selected);
            }
        }
    }, [searchParams, expenseTypeOptions]);

    const handleCreatedPresetChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const presetValue = e.target.value;
        setSelectedCreatedPreset(presetValue);

        const preset = datePresets.find((p) => p.value === presetValue);
        if (preset && presetValue !== 'custom') {
            const range = preset.getRange();
            setFormValues((prev) => ({
                ...prev,
                created_start_date: range.start,
                created_end_date: range.end,
            }));
        }
    };

    const handleUpdatedPresetChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const presetValue = e.target.value;
        setSelectedUpdatedPreset(presetValue);

        const preset = datePresets.find((p) => p.value === presetValue);
        if (preset && presetValue !== 'custom') {
            const range = preset.getRange();
            setFormValues((prev) => ({
                ...prev,
                updated_start_date: range.start,
                updated_end_date: range.end,
            }));
        }
    };

    const handleExpenseTypeChange = (
        selectedOptions: MultiValue<SelectOption>,
    ) => {
        setSelectedExpenseTypes([...selectedOptions]);
    };

    const hasActiveFilters =
        Object.values(formValues).some((value) => value !== '') ||
        selectedExpenseTypes.length > 0 ||
        selectedVendor !== null ||
        vendorSearch !== '';

    const handleResetFilters = () => {
        setFormValues(EMPTY_FORM_VALUES);
        setSelectedExpenseTypes([]);
        setSelectedVendor(null);
        setVendorSearch('');
        setSelectedTimeScale('');
        setSelectedCreatedPreset('');
        setSelectedUpdatedPreset('');
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const params = new URLSearchParams();

        // Add text filters
        if (formValues.client_name?.trim()) {
            params.append('client_name', formValues.client_name.trim());
        }
        if (formValues.ntaf_id?.trim()) {
            params.append('ntaf_id', formValues.ntaf_id.trim());
        }
        if (formValues.ap_invoice_number?.trim()) {
            params.append(
                'ap_invoice_number',
                formValues.ap_invoice_number.trim(),
            );
        }
        if (formValues.payee?.trim()) {
            params.append('payee', formValues.payee.trim());
        }
        if (selectedVendor) {
            params.append('vendor_id', String(selectedVendor.value));
        }
        if (formValues.status) {
            params.append('status', formValues.status);
        }
        if (formValues.source) {
            params.append('source', formValues.source);
        }
        if (formValues.amount_min) {
            params.append('amount_min', formValues.amount_min);
        }
        if (formValues.amount_max) {
            params.append('amount_max', formValues.amount_max);
        }

        // Add date ranges
        if (formValues.created_start_date) {
            params.append('created_date_start', formValues.created_start_date);
        }
        if (formValues.created_end_date) {
            params.append('created_date_end', formValues.created_end_date);
        }
        if (formValues.updated_start_date) {
            params.append('updated_date_start', formValues.updated_start_date);
        }
        if (formValues.updated_end_date) {
            params.append('updated_date_end', formValues.updated_end_date);
        }

        // Add expense types
        selectedExpenseTypes.forEach((expType) => {
            params.append('expense_type', expType.value);
        });

        // Append column selection only when it differs from the defaults,
        // keeping the common-case URL clean. Single comma-joined param to
        // match how the results page reads it.
        if (!isDefaultSelection(selectedColumns) && selectedColumns.length) {
            params.append('visible_columns', selectedColumns.join(','));
        }

        // Save filters to context and navigate to results
        setFilterParams(params.toString());
        setActiveAccordion(4);
        navigate(`/reports/fund-requests?${params.toString()}`);
    };

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Fund Requests
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus
                        className={`${active === id ? 'block' : 'hidden'}`}
                    />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='flex flex-wrap gap-[30px] lg:flex-nowrap'>
                        <div className='w-full lg:w-3/5'>
                            <h2 className='text-[#949599] text-[18px/22px]'>
                                FILTERS
                            </h2>

                            {/* Client Name and NTAF ID */}
                            <div className='mt-4 flex gap-4'>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='fr-client-name'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        Client Name
                                    </label>
                                    <input
                                        type='text'
                                        id='fr-client-name'
                                        placeholder='Medical Professional Name'
                                        value={formValues.client_name}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                client_name: e.target.value,
                                            }))
                                        }
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                                <div className='w-[150px]'>
                                    <label
                                        htmlFor='fr-ntaf-id'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        NTAF ID
                                    </label>
                                    <input
                                        type='text'
                                        id='fr-ntaf-id'
                                        placeholder='000000'
                                        value={formValues.ntaf_id}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                ntaf_id: e.target.value,
                                            }))
                                        }
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                            </div>

                            {/* AP Invoice Number */}
                            <div className='mt-4'>
                                <label
                                    htmlFor='fr-ap-invoice'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    AP Invoice Number
                                </label>
                                <input
                                    type='text'
                                    id='fr-ap-invoice'
                                    placeholder='XXXXXXXXXX'
                                    value={formValues.ap_invoice_number}
                                    onChange={(e) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            ap_invoice_number: e.target.value,
                                        }))
                                    }
                                    className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                />
                            </div>

                            {/* Payee and Vendor ID */}
                            <div className='mt-4 flex gap-4'>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='fr-payee'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        Payee
                                    </label>
                                    <Select
                                        id='fr-payee'
                                        menuPlacement='auto'
                                        options={vendorOptions}
                                        isClearable
                                        isLoading={isLoadingVendors}
                                        onInputChange={(value) =>
                                            setVendorSearch(value)
                                        }
                                        onChange={(option) =>
                                            setSelectedVendor(option)
                                        }
                                        value={selectedVendor}
                                        placeholder='Payee Name'
                                        noOptionsMessage={() =>
                                            vendorSearch.length < 2
                                                ? 'Type to search...'
                                                : 'No vendors found'
                                        }
                                        classNames={{
                                            control: () =>
                                                'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                            placeholder: () => 'text-[#949599]',
                                        }}
                                    />
                                </div>
                                <div className='w-[150px]'>
                                    <label
                                        htmlFor='fr-vendor-id'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        Vendor ID
                                    </label>
                                    <input
                                        type='text'
                                        id='fr-vendor-id'
                                        placeholder='000000'
                                        value={formValues.vendor_id}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                vendor_id: e.target.value,
                                            }))
                                        }
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                            </div>

                            {/* Expense Type (multi-select) */}
                            <div className='mt-4'>
                                <label
                                    htmlFor='fr-expense-type'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Expense Type (select multiple)
                                </label>
                                <Select
                                    id='fr-expense-type'
                                    menuPlacement='auto'
                                    options={expenseTypeOptions}
                                    isMulti
                                    onChange={handleExpenseTypeChange}
                                    value={selectedExpenseTypes}
                                    isLoading={isLoadingExpenseTypes}
                                    placeholder='Any'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                        multiValue: () =>
                                            'bg-[#F4F4F5] rounded-[4px] px-2 py-1 mr-1 mb-1',
                                        multiValueLabel: () => 'text-[14px]',
                                        multiValueRemove: () =>
                                            'hover:bg-[#F02D3A] hover:text-white rounded-r-[4px] px-1',
                                    }}
                                />
                            </div>

                            {/* Status */}
                            <div className='mt-4'>
                                <label
                                    htmlFor='fr-status'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Status
                                </label>
                                <Select
                                    id='fr-status'
                                    menuPlacement='auto'
                                    options={statusOptions}
                                    isClearable
                                    value={
                                        statusOptions.find(
                                            (opt) =>
                                                opt.value === formValues.status,
                                        ) || null
                                    }
                                    onChange={(option) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            status: option?.value || '',
                                        }))
                                    }
                                    placeholder='Any'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                    }}
                                />
                            </div>

                            {/* Time Scale */}
                            <div className='mt-4'>
                                <label
                                    htmlFor='fr-time-scale'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Time scale
                                </label>
                                <Select
                                    id='fr-time-scale'
                                    menuPlacement='auto'
                                    options={timeScaleOptions}
                                    isClearable
                                    value={
                                        timeScaleOptions.find(
                                            (opt) =>
                                                opt.value === selectedTimeScale,
                                        ) || null
                                    }
                                    onChange={(option) => {
                                        const presetValue = option?.value || '';
                                        setSelectedTimeScale(presetValue);
                                        const preset = datePresets.find(
                                            (p) => p.value === presetValue,
                                        );
                                        if (
                                            preset &&
                                            presetValue !== 'custom'
                                        ) {
                                            const range = preset.getRange();
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_start_date: range.start,
                                                created_end_date: range.end,
                                            }));
                                        }
                                    }}
                                    placeholder='All time'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                    }}
                                />
                            </div>

                            {/* Amount Range */}
                            <div className='mt-4 flex items-end gap-4'>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='fr-amount-min'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        Amount Min
                                    </label>
                                    <input
                                        type='text'
                                        id='fr-amount-min'
                                        placeholder='$0'
                                        value={
                                            formValues.amount_min
                                                ? formatCurrency(
                                                      formValues.amount_min,
                                                  )
                                                : ''
                                        }
                                        onChange={(e) => {
                                            const rawValue =
                                                parseCurrencyToNumber(
                                                    e.target.value,
                                                );
                                            setFormValues((prev) => ({
                                                ...prev,
                                                amount_min: rawValue,
                                            }));
                                        }}
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                                <span className='pb-3 text-gray-500'>to</span>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='fr-amount-max'
                                        className='block text-[12px]/[14px] font-extrabold'
                                    >
                                        Amount Max
                                    </label>
                                    <input
                                        type='text'
                                        id='fr-amount-max'
                                        placeholder='$0'
                                        value={
                                            formValues.amount_max
                                                ? formatCurrency(
                                                      formValues.amount_max,
                                                  )
                                                : ''
                                        }
                                        onChange={(e) => {
                                            const rawValue =
                                                parseCurrencyToNumber(
                                                    e.target.value,
                                                );
                                            setFormValues((prev) => ({
                                                ...prev,
                                                amount_max: rawValue,
                                            }));
                                        }}
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                            </div>

                            {/* Source (radio buttons) */}
                            <div className='mt-4'>
                                <label className='mb-2 block text-[12px]/[14px] font-extrabold'>
                                    Source
                                </label>
                                <div className='flex flex-col gap-2'>
                                    {sourceOptions.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className='flex cursor-pointer items-center gap-2'
                                        >
                                            <input
                                                type='radio'
                                                name='source'
                                                value={opt.value}
                                                checked={
                                                    formValues.source ===
                                                    opt.value
                                                }
                                                onChange={(e) =>
                                                    setFormValues((prev) => ({
                                                        ...prev,
                                                        source: e.target.value,
                                                    }))
                                                }
                                                className='h-4 w-4 border-gray-300 text-primary focus:ring-primary'
                                            />
                                            <span className='text-[14px]'>
                                                {opt.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Created Date Range */}
                            <div className='mt-4'>
                                <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                    Created
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='date'
                                        value={formValues.created_start_date}
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_start_date:
                                                    e.target.value,
                                            }));
                                            setSelectedCreatedPreset('custom');
                                            setSelectedTimeScale('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                    <span className='text-gray-500'>to</span>
                                    <input
                                        type='date'
                                        value={formValues.created_end_date}
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_end_date:
                                                    e.target.value,
                                            }));
                                            setSelectedCreatedPreset('custom');
                                            setSelectedTimeScale('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                </div>
                            </div>

                            {/* Updated Date Range */}
                            <div className='mt-4'>
                                <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                    Updated
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='date'
                                        value={formValues.updated_start_date}
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                updated_start_date:
                                                    e.target.value,
                                            }));
                                            setSelectedUpdatedPreset('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                    <span className='text-gray-500'>to</span>
                                    <input
                                        type='date'
                                        value={formValues.updated_end_date}
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                updated_end_date:
                                                    e.target.value,
                                            }));
                                            setSelectedUpdatedPreset('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='w-full lg:w-2/5'>
                            <div className='w-full rounded bg-[#F4F4F5] p-5'>
                                <h2 className='text-[#949599] text-[18px/22px]'>
                                    ABOUT THIS SEARCH
                                </h2>
                                <p className='mt-3 text-sm text-gray-600'>
                                    Search for fund requests by client name,
                                    NTAF ID, AP invoice number, payee/vendor,
                                    expense type, status, amount range, or date
                                    ranges.
                                </p>
                                <p className='mt-3 text-sm text-gray-600'>
                                    <strong>Source filter:</strong>
                                </p>
                                <ul className='mt-2 list-inside list-disc text-sm text-gray-600'>
                                    <li>
                                        <strong>Web</strong> - Requests created
                                        by patients/clients
                                    </li>
                                    <li>
                                        <strong>Manually Entered</strong> -
                                        Requests created by staff
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Column Configuration */}
                    <div className='mt-8 border-t border-gray-200 pt-6'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setShowColumns((s) => !s)}
                                className='flex items-center gap-2 text-[12px] font-bold text-[#949599]'
                            >
                                COLUMNS ({selectedColumns.length} selected)
                                {showColumns ? (
                                    <FaMinus size={10} />
                                ) : (
                                    <FaPlus size={10} />
                                )}
                            </button>
                            {!isDefaultSelection(selectedColumns) && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSelectedColumns(DEFAULT_VISIBLE_COLUMNS)
                                    }
                                    className='text-[12px] text-gray-500 underline hover:text-gray-700'
                                >
                                    Reset to default
                                </button>
                            )}
                        </div>
                        {showColumns && (
                            <div className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                                {ALL_AVAILABLE_COLUMNS.map((column) => (
                                    <label
                                        key={column.key}
                                        className='flex cursor-pointer items-center gap-2 text-sm'
                                    >
                                        <input
                                            type='checkbox'
                                            checked={selectedColumns.includes(
                                                column.key,
                                            )}
                                            onChange={() =>
                                                toggleColumn(column.key)
                                            }
                                            className='rounded border-gray-300 text-primary focus:ring-primary'
                                        />
                                        <span className='text-gray-700'>
                                            {column.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Search Fund Requests
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FundRequestAccordionItem;
